import Header from '@/components/Header'
import useDocumentTitle from '@/components/Helmet'
import LocationPicker from '@/components/GoogleMap/LocationPicker'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import MultiImageUpload from '@/components/ui/multiFileUpload'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import API_URL from '@/config'
import { useToast } from '@/hooks/use-toast'
import axiosInstance from '@/lib/axiosInstance'
import { zodResolver } from '@hookform/resolvers/zod'
import { Box, Card, CardMedia, Grid, Tooltip, Typography } from '@mui/material'
import { ArrowLeft, X } from 'lucide-react'
import React, { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useLocation, useParams } from 'wouter'
import { z } from 'zod'
import { LocationSelector } from '@/components/Locations/Index'

const validationSchema = z.object({
    title: z.string().min(1, "Title is required").max(100, "Title cannot be more than 100 characters"),
    description: z.string().min(1, "Description is required").max(500, "Description cannot be more than 500 characters"),
    price: z.coerce.string().min(1, "Price is required"),
    address: z.string().min(1, "Address is required").max(100, "Address cannot be more than 100 characters"),
    category: z.string().min(1, "Category is required"),
    propertyType: z.string().min(1, "Property type is required"),
    pincode: z.string().min(5, "Pincode is required").max(6, "Pincode cannot be more than 6 characters"),
    city: z.string(),
    state: z.string().min(1, "State is required"),
    country: z.string().min(1, "country is required"),
    status: z.string().min(1, "Status is required"),
    beds: z.coerce.number().min(1, "Number of bedrooms is required"),
    baths: z.coerce.number().min(1, "Number of bathrooms is required"),
    size: z.coerce.number().min(1, "Size is required"),
    isNewBuild: z.boolean(),
    isActive: z.boolean(),
    location: z.object({
        type: z.string(),
        coordinates: z.array(z.number()),
    }),
    agent: z.object({
        name: z.string().min(1, "Agent name is required"),
        company: z.string().min(1, "Agent name is required"),
        email: z.string().email("Please enter a valid email address"),
        phone: z.string().min(1, "Phone number is required"),
    })
})
const Add = () => {
    const { id } = useParams();
    useDocumentTitle(id ? "Property Update" : "Property Add");
    const { toast } = useToast();
    const [location, navigate] = useLocation();

    const { control, handleSubmit, formState: { errors }, setValue, getValues, setError } = useForm({
        resolver: zodResolver(validationSchema),
        defaultValues: {
            title: "",
            description: "",
            price: "",
            address: "",
            category: "",
            propertyType: "",
            pincode: "",
            city: "",
            state: "",
            country: "",
            status: "",
            beds: 0,
            baths: 0,
            size: 0,
            isNewBuild: false,
            isActive: false,
            location: { type: 'Point', coordinates: [-6.2603, 53.3498] },
            agent: {
                name: "",
                company: "",
                email: "",
                phone: ""
            }
        },
    });
    const [locations, setLocation] = useState("");
    const [latLang, setLatLang] = useState<[number, number]>([-6.2603, 53.3498]);
    const [images, setImages] = useState<File[]>([]);
    const [videos, setVideos] = useState<File[]>([]);
    const [imagePreview, setImagePreview] = useState<string[]>([]);
    const [videoPreview, setVideoPreview] = useState<string[]>([]);

    useEffect(() => {
        setValue("location", { type: 'Point', coordinates: [latLang[0], latLang[1] ] });
    }, [latLang, setValue]);


    const fetchPropertyById = async () => {
        try {
            const response = await axiosInstance.get(`/api/properties/${id}`);
            const property = response.data?.data;
            let location = property.location;
            setValue("title", property.title);
            setValue("description", property.description);
            setValue("price", property.price);
            setValue("address", property.address);
            setValue("category", property.category);
            setValue("propertyType", property.propertyType);
            setValue("pincode", property.pincode);
            setValue("city", property.city);
            setValue("state", property.state);
            setValue("country", property.country);
            setValue("status", property.status);
            setValue("beds", property.beds);
            setValue("baths", property.baths);
            setValue("size", property.size);
            setValue("isNewBuild", property.isNewBuild);
            setValue("isActive", property.isActive);
            setValue("location", location);
            setValue("agent", property.agent);
            setImagePreview(property.images)
            setVideoPreview(property.videos)
            setLatLang([location.coordinates?.[0], location.coordinates?.[1]]);
        } catch (error: any) {
            toast({
                variant: "destructive",
                title: "Error!",
                description: error?.response?.data?.message,
            })
        }
    }

    useEffect(() => {
        if (id) {
            fetchPropertyById()
        }
    }, [id])

    const submitForm = async (data: any) => {
        try {
            const formData = new FormData();
            images.forEach((img: any) => {
                formData.append("images", img);
            });
            videos.forEach((video: any) => {
                formData.append("videos", video);
            })
            Object.keys(data).forEach((key) => {
                if (typeof data[key] === "object") {
                    formData.append(key, JSON.stringify(data[key]));
                } else {
                    formData.append(key, data[key]);
                }
            });
            if (id) {
                await axiosInstance.put(`/api/properties/${id}`, formData, {
                    headers: {
                        "Content-Type": "multipart/form-data",
                    }
                })
                    .then((res) => {
                        if (res?.data?.success) {
                            toast({
                                variant: "success",
                                title: "Success",
                                description: "Property updated successfully",
                            })
                            navigate("/seller/property")
                        }
                        else {
                            toast({
                                variant: "destructive",
                                title: "Error!",
                                description: res?.data?.message,
                            })
                        }
                    })
            }
            else {
                await axiosInstance.post("/api/properties", formData, {
                    headers: {
                        "Content-Type": "multipart/form-data",
                    },
                })
                    .then((res) => {
                        if (res?.data?.success) {
                            toast({
                                variant: "success",
                                title: "Success",
                                description: "Property added successfully",
                            })
                            navigate("/seller/property")
                        }
                        else {
                            toast({
                                variant: "destructive",
                                title: "Error!",
                                description: res?.data?.message,
                            })
                        }
                    })
            }
        } catch (error: any) {
            toast({
                variant: "destructive",
                title: "Error!",
                description: error?.response?.data?.message,
            })
        }
    }

    const handleDeleteImage = async (image: string) => {
        try {
            await axiosInstance.delete(`/api/properties/images/${image}/${id}`)
                .then((res) => {
                    if (res?.data?.success) {
                        toast({
                            variant: "success",
                            title: "Success",
                            description: "Image deleted successfully",
                        })
                        fetchPropertyById()
                    }
                    else {
                        toast({
                            variant: "destructive",
                            title: "Error!",
                            description: res?.data?.message,
                        })
                    }
                })
        } catch (error: any) {
            toast({
                variant: "destructive",
                title: "Error!",
                description: error?.response?.data?.message,
            })
        }
    }
    
    const handleDeleteVideo = async (video: string) => {
        try {
            await axiosInstance.delete(`/api/properties/videos/${video}/${id}`)
                .then((res) => {
                    if (res?.data?.success) {
                        toast({
                            variant: "success",
                            title: "Success",
                            description: "Video deleted successfully",
                        })
                        fetchPropertyById()
                    }
                    else {
                        toast({
                            variant: "destructive",
                            title: "Error!",
                            description: res?.data?.message,
                        })
                    }
                })
        } catch (error: any) {
            toast({
                variant: "destructive",
                title: "Error!",
                description: error?.response?.data?.message,
            })
        }
    }
    return (
        <div className="min-h-screen bg-gray-50">
            <Header />
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <Card sx={{ p: 2 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                        <Button onClick={() => navigate("/seller/property")}> <ArrowLeft /> Back</Button>
                        <Typography sx={{ textAlign: "center", fontWeight: 600, fontSize: 24 }}>{id ? "Update" : "Add"} Property</Typography>
                        <Box>

                        </Box>
                    </Box>
                    <form onSubmit={handleSubmit(submitForm)}>
                        <Grid container spacing={2} sx={{ mt: 1 }}>
                            <Grid size={{ md: 12, sm: 12, xs: 12 }}>
                                <Controller
                                    name="title"
                                    control={control}
                                    render={({ field }) => (
                                        <>
                                            <Label htmlFor="title">Title</Label>
                                            <Input {...field} placeholder="Title" id="title" />
                                        </>
                                    )}
                                />
                                {
                                    errors.title && (
                                        <Typography sx={{ color: "red" }}>{errors.title.message}</Typography>
                                    )
                                }
                            </Grid>
                            <Grid size={{ md: 12, sm: 12, xs: 12 }}>
                                <Controller
                                    name="description"
                                    control={control}
                                    render={({ field }) => (
                                        <>
                                            <Label htmlFor="description">Description</Label>
                                            <Textarea {...field} placeholder="Description" id="description" />
                                        </>
                                    )}
                                />
                                {
                                    errors.description && (
                                        <Typography sx={{ color: "red" }}>{errors.description.message}</Typography>
                                    )
                                }
                            </Grid>
                            <Grid size={{ md: 6, sm: 12, xs: 12 }}>
                                <Controller
                                    name="price"
                                    control={control}
                                    render={({ field }) => (
                                        <>
                                            <Label htmlFor="price">Price</Label>
                                            <Input {...field} placeholder="Price" type='number' id="price" />
                                        </>
                                    )}
                                />
                                {
                                    errors.price && (
                                        <Typography sx={{ color: "red" }}>{errors.price.message}</Typography>
                                    )
                                }
                            </Grid>
                            <Grid size={{ md: 6, sm: 12, xs: 12 }}>
                                <Controller
                                    name="category"
                                    control={control}
                                    render={({ field }) => (
                                        <>
                                            <Label htmlFor="category">Category</Label>
                                            <Select value={field.value} onValueChange={field.onChange}>
                                                <SelectTrigger className="text-gray-900 text-sm" id="category" aria-labelledby="category" data-testid="select-hero-bathrooms">
                                                    <SelectValue placeholder="Property Category" data-testid="select-hero-bathroomsType" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value='Buy'>Buy</SelectItem>
                                                    <SelectItem value='Sold'>Sold</SelectItem>
                                                    <SelectItem value='Rent'>Rent</SelectItem>
                                                    <SelectItem value='Share'>Share</SelectItem>
                                                    <SelectItem value='New Homes'>New Homes</SelectItem>
                                                    <SelectItem value='Commercial'>Commercial</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </>
                                    )}
                                />
                                {
                                    errors.category && (
                                        <Typography sx={{ color: "red" }}>{errors.category.message}</Typography>
                                    )
                                }

                            </Grid>


                            <Grid size={{ md: 6, sm: 12, xs: 12 }}>
                                <Controller
                                    name="address"
                                    control={control}
                                    render={({ field }) => (
                                        <>
                                            <Label htmlFor="address">Address</Label>
                                            <Input {...field} placeholder="Address" id="address" />
                                        </>
                                    )}
                                />
                                {
                                    errors.address && (
                                        <Typography sx={{ color: "red" }}>{errors.address.message}</Typography>
                                    )
                                }
                            </Grid>

                            <LocationSelector control={control} errors={errors} setValue={setValue} toast={toast} setError={setError} getValues={getValues} />

                            <Grid size={{ md: 6, sm: 12, xs: 12 }}>
                                <Controller
                                    name="propertyType"
                                    control={control}
                                    render={({ field }) => (
                                        <>
                                            <Label htmlFor="propertyType">Property Type</Label>
                                            <Select value={field.value} onValueChange={field.onChange}>
                                                <SelectTrigger className="text-gray-900 text-sm" id='propertyType' data-testid="select-hero-bathrooms">
                                                    <SelectValue placeholder="Property Type" data-testid="select-hero-bathroomsType" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value='apartment'>Apartment</SelectItem>
                                                    <SelectItem value='house'>House</SelectItem>
                                                    <SelectItem value='studio'>Studio</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </>
                                    )}
                                />
                                {
                                    errors.propertyType && (
                                        <Typography sx={{ color: "red" }}>{errors.propertyType.message}</Typography>
                                    )
                                }

                            </Grid>

                            <Grid size={{ md: 6, sm: 12, xs: 12 }}>
                                <Controller
                                    name="status"
                                    control={control}
                                    render={({ field }) => (
                                        <>
                                            <Label htmlFor="status">Status</Label>
                                            <Select value={field.value} onValueChange={field.onChange}>
                                                <SelectTrigger className="text-gray-900 text-sm" id='status' data-testid="select-hero-bathrooms">
                                                    <SelectValue placeholder="Status" data-testid="select-hero-bathroomsType" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value='for sale'>For Sale</SelectItem>
                                                    <SelectItem value='sale agreed'>Sale Agreed</SelectItem>
                                                    <SelectItem value='sold'>Sold</SelectItem>
                                                    <SelectItem value='withdrawn'>Withdrawn</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </>
                                    )}
                                />
                                {
                                    errors.status && (
                                        <Typography sx={{ color: "red" }}>{errors.status.message}</Typography>
                                    )
                                }

                            </Grid>

                            <Grid size={{ md: 6, sm: 12, xs: 12 }}>
                                <Controller
                                    name="beds"
                                    control={control}
                                    render={({ field }) => (
                                        <>
                                            <Label htmlFor="beds">Bedrooms</Label>
                                            <Input {...field} type='number' placeholder="Please enter no. of Bedrooms" id="beds" />
                                        </>
                                    )}
                                />
                                {
                                    errors.beds && (
                                        <Typography sx={{ color: "red" }}>{errors.beds.message}</Typography>
                                    )
                                }
                            </Grid>

                            <Grid size={{ md: 6, sm: 12, xs: 12 }}>
                                <Controller
                                    name="baths"
                                    control={control}
                                    render={({ field }) => (
                                        <>
                                            <Label htmlFor="baths">Bathrooms</Label>
                                            <Input {...field} type='number' placeholder="Please enter no. of Bathrooms" id="baths" />
                                        </>
                                    )}
                                />
                                {
                                    errors.baths && (
                                        <Typography sx={{ color: "red" }}>{errors.baths.message}</Typography>
                                    )
                                }
                            </Grid>

                            <Grid size={{ md: 6, sm: 12, xs: 12 }}>
                                <Controller
                                    name="size"
                                    control={control}
                                    render={({ field }) => (
                                        <>
                                            <Label htmlFor="size">Size (sq meter)</Label>
                                            <Input {...field} type='number' placeholder="Please enter size(sq meter)" id="size" />
                                        </>
                                    )}
                                />
                                {
                                    errors.size && (
                                        <Typography sx={{ color: "red" }}>{errors.size.message}</Typography>
                                    )
                                }
                            </Grid>

                            {/* Checkobox */}
                            <Grid size={{ md: 6, sm: 12, xs: 12 }}>
                                <Controller
                                    name="isNewBuild"
                                    control={control}
                                    render={({ field }) => (
                                        <div className="flex items-start space-x-2">
                                            <Checkbox
                                                id="terms"
                                                checked={field.value}
                                                onCheckedChange={(checked) => field.onChange(checked)}
                                                data-testid="checkbox-terms"
                                            />
                                            <Label htmlFor="terms" className="text-sm text-gray-600 leading-5">
                                                Is New Building?
                                            </Label>
                                        </div>

                                    )}
                                />
                                {
                                    errors.isNewBuild && (
                                        <Typography sx={{ color: "red" }}>{errors.isNewBuild.message}</Typography>
                                    )
                                }
                            </Grid>
                            <Grid size={{ md: 6, sm: 12, xs: 12 }}>
                                <Controller
                                    name="isActive"
                                    control={control}
                                    render={({ field }) => (
                                        <div className="flex items-start space-x-2">
                                            <Checkbox
                                                id="active"
                                                checked={field.value}
                                                onCheckedChange={(checked) => field.onChange(checked)}
                                                data-testid="checkbox-active"
                                            />
                                            <Label htmlFor="active" className="text-sm text-gray-600 leading-5">
                                                Is Active?
                                            </Label>
                                        </div>

                                    )}
                                />
                                {
                                    errors.isActive && (
                                        <Typography sx={{ color: "red" }}>{errors.isActive.message}</Typography>
                                    )
                                }
                            </Grid>
                            <Grid size={{ md: 12, sm: 12, xs: 12 }}>
                                <>
                                    <MultiImageUpload setImages={setImages} images={images} title="Upload Your Images" accept="image/*" support="jpg, jpeg, png" type="image" />
                                    {imagePreview.length > 0 && <Box>

                                        <Typography sx={{ fontSize: 18, fontWeight: 600, mb: 1 }}>Image Preview</Typography>
                                        <Grid container spacing={2}>
                                            {
                                                imagePreview?.map((img: any, index: number) => {
                                                    const ImageLink = API_URL + "/getImage/image/" + img
                                                    return (
                                                        <Grid size={{ md: 2, sm: 4, xs: 4 }} key={index}>
                                                            <Card sx={{ position: "relative", p: 2, textAlign: "center" }}>
                                                                <Box sx={{ position: "absolute", top: 0, right: 0, cursor: "pointer" }}>
                                                                    <Tooltip title="Remove Image">
                                                                        <X onClick={() => handleDeleteImage(img)} color="red" />
                                                                    </Tooltip>
                                                                </Box>
                                                                <CardMedia
                                                                    component="img"
                                                                    height="50"
                                                                    image={ImageLink}
                                                                    alt={`preview-${index}`}
                                                                />
                                                            </Card>
                                                        </Grid>
                                                    )
                                                })
                                            }
                                        </Grid></Box>}
                                </>
                            </Grid>

                            <Grid size={{ md: 12, sm: 12, xs: 12 }}>
                                <>
                                    <MultiImageUpload setVideos={setVideos} images={videos} title="Upload Your Videos" accept="video/*" support="mp4" type="video" />
                                    {videoPreview.length > 0 && <Box>

                                        <Typography sx={{ fontSize: 18, fontWeight: 600, mb: 1 }}>Video Preview</Typography>
                                        <Grid container spacing={2}>
                                            {
                                                videoPreview?.map((video: any, index: number) => {
                                                    const VideoLink = API_URL + "/getVideo/video/" + video
                                                    return (
                                                        <Grid size={{ md: 2, sm: 4, xs: 4 }} key={index}>
                                                            <Card sx={{ position: "relative", p: 2, textAlign: "center" }}>
                                                                <Box sx={{ position: "absolute", top: 0, right: 0, cursor: "pointer" }}>
                                                                    <Tooltip title="Remove Image">
                                                                        <X onClick={() => handleDeleteVideo(video)} color="red" />
                                                                    </Tooltip>
                                                                </Box>
                                                                <video
                                                                    controls
                                                                    width="100%"
                                                                    height="120"
                                                                    style={{ borderRadius: 8 }}
                                                                    src={VideoLink}
                                                                />
                                                            </Card>
                                                        </Grid>
                                                    )
                                                })
                                            }
                                        </Grid></Box>}
                                </>
                            </Grid>

                            <Grid size={{ md: 12, sm: 12, xs: 12 }}>
                                <LocationPicker
                                    setLocation={setLocation}
                                    location={locations}
                                    setLatLang={setLatLang}
                                    latLang={latLang}
                                />
                            </Grid>

                            <Grid size={{ md: 12, sm: 12, xs: 12 }}>
                                <Typography sx={{ fontSize: 18, fontWeight: 600, mt: 1 }}>Agent Details</Typography>
                            </Grid>

                            <Grid size={{ md: 6, sm: 12, xs: 12 }}>
                                <Controller
                                    name="agent.name"
                                    control={control}
                                    render={({ field }) => (
                                        <>
                                            <Label htmlFor="agent_name">Agent Name</Label>
                                            <Input {...field} type='text' placeholder="Please enter agent name" id="agent_name" />
                                        </>
                                    )}
                                />
                                {
                                    errors.agent?.name && (
                                        <Typography sx={{ color: "red" }}>{errors.agent.name.message}</Typography>
                                    )
                                }
                            </Grid>
                            <Grid size={{ md: 6, sm: 12, xs: 12 }}>
                                <Controller
                                    name="agent.email"
                                    control={control}
                                    render={({ field }) => (
                                        <>
                                            <Label htmlFor="agent_email">Agent Email</Label>
                                            <Input {...field} type='text' placeholder="Please enter agent email" id="agent_email" />
                                        </>
                                    )}
                                />
                                {
                                    errors.agent?.email && (
                                        <Typography sx={{ color: "red" }}>{errors.agent.email.message}</Typography>
                                    )
                                }
                            </Grid>
                            <Grid size={{ md: 6, sm: 12, xs: 12 }}>
                                <Controller
                                    name="agent.phone"
                                    control={control}
                                    render={({ field }) => (
                                        <>
                                            <Label htmlFor="agent_phone">Agent Phone</Label>
                                            <Input {...field} type='text' placeholder="Please enter agent phone" id="agent_phone" />
                                        </>
                                    )}
                                />
                                {
                                    errors.agent?.phone && (
                                        <Typography sx={{ color: "red" }}>{errors.agent.phone.message}</Typography>
                                    )
                                }
                            </Grid>
                            <Grid size={{ md: 6, sm: 12, xs: 12 }}>
                                <Controller
                                    name="agent.company"
                                    control={control}
                                    render={({ field }) => (
                                        <>
                                            <Label htmlFor="agent_company">Agent Company Name</Label>
                                            <Input {...field} type='text' placeholder="Please enter agent company name" id="agent_company" />
                                        </>
                                    )}
                                />
                                {
                                    errors.agent?.company && (
                                        <Typography sx={{ color: "red" }}>{errors.agent.company.message}</Typography>
                                    )
                                }
                            </Grid>


                            <Grid size={{ md: 12, sm: 12, xs: 12 }} >
                                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                                    <Button type="button" className='bg-red-600' onClick={() => navigate("/seller/property")}>Cancel</Button>
                                    <Button type="submit">
                                        {id ? "Update" : "Add"}
                                    </Button>
                                </Box>
                            </Grid>
                        </Grid>
                    </form>
                </Card>
            </section>
        </div>
    )
}

export default Add